// ============================================================================
// Shared Styled Components - WholesaleHub Reusable Components
// ============================================================================
import styled, { css, keyframes } from 'styled-components'
import { Table, Modal } from 'react-bootstrap'
import { colors, gradients, shadows, transitions, radius, transform } from './colors'



// ============================================================================
// Button Components
// ============================================================================

// ----------------------------------------------------------------------------
// Base Button (shared styles, not exported)
// ----------------------------------------------------------------------------
const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  white-space: nowrap;
  border: none;
  border-radius: var(--radius);
  padding: 0.5rem 1.1rem;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  transition: ${transitions.default};
  cursor: pointer;
  text-decoration: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: ${shadows.primary};
  }

  @media (max-width: 576px) {
    padding: 0.45rem 0.9rem;
    font-size: 0.82rem;
  }
`

// ----------------------------------------------------------------------------
// PrimaryButton, bg primary gradient, white text
// ----------------------------------------------------------------------------
export const PrimaryButton = styled(BaseButton)`
  background: ${gradients.primary};
  color: ${colors.white};

  &:hover {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }

  &:disabled {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }
`

// ----------------------------------------------------------------------------
// AccentButton, bg accent, white text
// ----------------------------------------------------------------------------
export const AccentButton = styled(BaseButton)`
  background: ${gradients.accent};
  color: ${colors.white};

  &:hover:not(:disabled) {
    color: ${colors.white};
    box-shadow: ${shadows.accent};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.accent};
  }
`

// ----------------------------------------------------------------------------
// WhiteButton, bg white, primary text
// ----------------------------------------------------------------------------
export const WhiteButton = styled(BaseButton)`
  background: ${colors.white};
  color: ${colors.primary};

  &:hover:not(:disabled) {
    color: ${colors.primary};
    box-shadow: ${shadows.gray};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }
`

// ----------------------------------------------------------------------------
// GrayButton, white bg, border, cancel/secondary action
// ----------------------------------------------------------------------------
export const GrayButton = styled(BaseButton)`
  background: ${gradients.gray};
  color: ${colors.white};

  &:hover:not(:disabled) {
    box-shadow: ${shadows.gray};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }
`

// ----------------------------------------------------------------------------
// DangerButton, for destructive actions
// ----------------------------------------------------------------------------
export const DangerButton = styled(BaseButton)`
  background: ${colors.danger};
  color: ${colors.white};
  box-shadow: ${shadows.danger};

  &:hover:not(:disabled) {
    box-shadow: ${shadows.gray};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }
`

// ----------------------------------------------------------------------------
// SuccessButton, for positive actions
// ----------------------------------------------------------------------------
export const SuccessButton = styled(BaseButton)`
  background: ${colors.success};
  color: ${colors.white};
  box-shadow: ${shadows.success};

  &:hover:not(:disabled) {
    box-shadow: ${shadows.gray};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }
`

// ----------------------------------------------------------------------------
// HelpButton — icon-only, no background, muted; for opening help/info modals
// ----------------------------------------------------------------------------
export const HelpButton = styled.button`
  background: none;
  border: none;
  color: ${colors.textLightModeMuted};
  font-size: 1.15rem;
  padding: 0.2rem;
  line-height: 1;
  border-radius: 50%;
  cursor: pointer;
  transition: color 0.15s;

  &:hover { color: ${colors.textLightMode}; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid ${colors.borderLight}; }
`

// ----------------------------------------------------------------------------
// ButtonIcon, styled span for icons
// ----------------------------------------------------------------------------
export const ButtonIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 18px;
`

// ----------------------------------------------------------------------------
// TableHeaderButton, for buttons in table headers, smaller padding
// ----------------------------------------------------------------------------

export const TableButton = styled.button`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: ${transitions.default};
`

// ----------------------------------------------------------------------------
// TableButtonPrimary, for primary actions in table rows, smaller padding
// ----------------------------------------------------------------------------
export const TableButtonPrimary = styled(TableButton)`
  background: ${gradients.primary};
  color: ${colors.white};

  &:hover {
    transform: ${transform.default};
    box-shadow: ${shadows.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

// ----------------------------------------------------------------------------
// TableButtonAccent, bg accent, white text
// ----------------------------------------------------------------------------
export const TableButtonAccent = styled(TableButton)`
  background: ${gradients.accent};
  color: ${colors.white};

  &:hover:not(:disabled) {
    color: ${colors.white};
    box-shadow: ${shadows.accent};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.accent};
  }
`

// ----------------------------------------------------------------------------
// TableButtonWhite, bg white, primary text
// ----------------------------------------------------------------------------
export const TableButtonWhite = styled(TableButton)`
  background: ${colors.white};
  color: ${colors.primary};

  &:hover:not(:disabled) {
    color: ${colors.primary};
    box-shadow: ${shadows.gray};
    transform: ${transform.default};
  }

  &:active:not(:disabled) {
    transform: ${transform.default};
    box-shadow: ${shadows.gray};
  }
`



// ============================================================================
// Card Components
// ============================================================================

export const StyledCard = styled.div`
  background: white;
  border-radius: var(--radius);
  box-shadow: ${shadows.card};
  border: 1px solid ${colors.borderLight};
  overflow: hidden;
`

export const CardHeader = styled.div`
  background: ${gradients.primary};
  color: white;
  padding: 1rem 1.5rem;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const CardHeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  i {
    font-size: 1.1rem;
  }
`

export const CardBody = styled.div`
  padding: 1.5rem;
`



// ============================================================================
// Table Components
// ============================================================================

export const StyledTable = styled(Table)`
  margin-bottom: 0;

  thead {
    background: ${colors.backgroundAlt};

    th {
      color: ${colors.textLightModeMuted};
      font-weight: 600;
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      padding: 1rem;
      border-bottom: 2px solid ${colors.borderLight};
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      transition: ${transitions.fast};

      &:hover {
        background: ${colors.hoverTint};
      }

      &.inactive {
        opacity: 0.6;
        background: ${colors.backgroundAlt};
      }

      td {
        padding: 1rem;
        vertical-align: middle;
        color: ${colors.textLightMode};
        border-bottom: 1px solid ${colors.borderLight};
      }
    }
  }
`

export const CodeText = styled.code`
  background: ${colors.backgroundAlt};
  padding: 0.2rem 0.5rem;
  border-radius: 0.3rem;
  font-size: 0.85rem;
  color: ${colors.primary};
  font-weight: 500;
`



// ============================================================================
// Badge Components
// ============================================================================

export const StatusBadge = styled.span<{ $variant: 'success' | 'secondary' | 'danger' | 'info' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: var(--radius);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;

  ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return `
          background: rgba(40, 167, 69, 0.1);
          color: ${colors.success};
        `
      case 'danger':
        return `
          background: rgba(220, 53, 69, 0.1);
          color: ${colors.danger};
        `
      case 'warning':
        return `
          background: rgba(255, 193, 7, 0.15);
          color: #856404;
        `
      case 'info':
        return `
          background: rgba(0, 163, 218, 0.1);
          color: ${colors.primaryLight};
        `
      default:
        return `
          background: rgba(108, 117, 125, 0.1);
          color: ${colors.textLightModeMuted};
        `
    }
  }}
`



// ============================================================================
// Search & Filter Components
// ============================================================================

export const FilterSelect = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: var(--radius);
  font-size: 0.9rem;
  cursor: pointer;
  min-width: 140px;

  option {
    background: ${colors.primaryDark};
    color: white;
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
  }
`

export const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`



// ============================================================================
// Pagination Components
// ============================================================================

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${colors.borderLight};
`




// ============================================================================
// Loading Components
// ============================================================================

export const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${colors.primary};

  p {
    margin-top: 1rem;
    color: ${colors.textLightModeMuted};
  }
`



// ============================================================================
// Modal Components
// ============================================================================

// StyledModal, gradient header, used by admin/feature modals
export const StyledModal = styled(Modal)`
  --bs-modal-border-radius: var(--radius);

  .modal-content {
    border: none;
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: ${shadows.cardLarge};
  }

  .modal-header {
    background: ${gradients.primary};
    color: white;
    border-bottom: none;
    padding: 1.25rem 1.5rem;
    border-radius: var(--radius) var(--radius) 0 0;

    .modal-title {
      font-weight: 600;
      font-size: 1.1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-close {
      filter: brightness(0) invert(1);
      opacity: 0.8;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    padding: 1.5rem;
    background: white;
    overscroll-behavior: contain;

    @media (max-width: 576px) {
      padding: 1rem;
    }
  }

  .modal-footer {
    background: ${colors.backgroundAlt};
    border-top: 1px solid ${colors.borderLight};
    padding: 0.75rem 1.25rem;
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
`



// ============================================================================
// Form Components
// ============================================================================

export const FormGroup = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const StyledFormLabel = styled.label`
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${colors.textLightMode};
  margin-bottom: 0.5rem;

  .required {
    color: ${colors.danger};
    margin-left: 0.25rem;
  }
`

export const FormLabel = StyledFormLabel

export const FormInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid ${colors.borderLight};
  border-radius: var(--radius);
  font-size: 16px !important;
  color: ${colors.textLightMode};
  transition: ${transitions.fast};

  &::placeholder {
    color: ${colors.textLightModeMuted};
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.hoverTint};
  }

  &:disabled {
    background: ${colors.backgroundAlt};
    cursor: not-allowed;
  }
`

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid ${colors.borderLight};
  border-radius: var(--radius);
  font-size: 16px !important;
  color: ${colors.textLightMode};
  transition: ${transitions.fast};
  resize: vertical;
  min-height: 100px;

  &::placeholder {
    color: ${colors.textLightModeMuted};
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.hoverTint};
  }
`

export const FormSelect = styled.select`
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid ${colors.borderLight};
  border-radius: var(--radius);
  font-size: 16px !important;
  color: ${colors.textLightMode};
  background: white;
  cursor: pointer;
  transition: ${transitions.fast};

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.hoverTint};
  }
`

export const FieldError = styled.span`
  color: ${colors.danger};
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
`

export const FormCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: ${colors.textLightMode};
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${colors.primary};
  }
`

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  ${FormGroup} {
    margin-bottom: 0;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`


export const FormHint = styled.small`
  display: block;
  color: ${colors.textLightModeMuted};
  font-size: 0.8rem;
  margin-top: 0.35rem;

  a {
    color: ${colors.primary};
    text-decoration: underline;
    &:hover { color: ${colors.primaryLight}; }
  }
`

export const CharCount = styled.span<{ $warning?: boolean; $danger?: boolean }>`
  display: block;
  font-size: 0.75rem;
  color: ${p => p.$danger ? colors.danger : p.$warning ? colors.warning : colors.textLightModeMuted};
  margin: 0;
  margin-bottom: -0.9rem;
  text-align: right;
`



// ============================================================================
// Page Header Components
// Shared mini-header: title + icon on the left, actions on the right
// ============================================================================

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const PageTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${colors.textLightMode};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: ${colors.primary};
    font-size: 1.1rem;
  }
`

export const PageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

// ============================================================================
// Section Components
// ============================================================================

export const Section = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${colors.borderLight};

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`

export const SectionTitle = styled.h6`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    font-size: 1rem;
  }
`



// ============================================================================
// Hero Background
// Gradient bg with decorative bubble orbs — used by LandingPage hero section
// and FeaturedProductCard hero preview so they always match exactly.
// ============================================================================

export const bubbleOrbs = css`
  &::before {
    content: '';
    position: absolute;
    top: -120px; right: -120px;
    width: 500px; height: 500px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -140px; left: -80px;
    width: 420px; height: 420px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
    pointer-events: none;
  }
`

// 5-point star clip-path — two large decorative stars for the Americana theme
export const starOrbs = css`
  &::before {
    content: '';
    position: absolute;
    top: -60px; left: -60px;
    width: 320px; height: 320px;
    clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -80px; right: -60px;
    width: 260px; height: 260px;
    clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
    background: rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }
`

export const HeroBg = styled.div<{ $stars?: boolean }>`
  background: ${gradients.primary};
  position: relative;
  overflow: hidden;
  ${p => p.$stars && starOrbs}
`



// ============================================================================
// Section Divider
// Animated gradient line — drop between any two sections instead of using
// ::after pseudo-elements on individual components.
// ============================================================================

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 6px ${colors.accent}, 0 0 18px ${colors.accent}, 0 0 32px rgba(0, 166, 118, 0.3);
    opacity: 0.8;
  }
  50% {
    box-shadow: 0 0 10px ${colors.accentLight}, 0 0 26px ${colors.accent}, 0 0 50px rgba(0, 166, 118, 0.5);
    opacity: 1;
  }
`

export const ShimmerDivider = styled.hr`
  border: none;
  margin: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${colors.accentDark} 15%,
    ${colors.accentLight} 50%,
    ${colors.accentDark} 85%,
    transparent 100%
  );
  box-shadow: 0 0 6px ${colors.accent}, 0 0 18px ${colors.accent}, 0 0 32px rgba(0, 166, 118, 0.3);
  animation: ${glowPulse} 2.5s ease-in-out infinite;
`


// ============================================================================
// Pagination Controls
// Gold standard 4-button pagination (First, Previous, Next, Last)
// ============================================================================

export const PaginationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid ${colors.borderLight};
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`

export const ResultCount = styled.span`
  color: ${colors.textLightModeMuted};
  font-size: 0.85rem;
`

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

export const PageButton = styled.button`
  background: white;
  color: ${colors.textLightMode};
  border: 1px solid ${colors.borderLight};
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  width: 2rem;
  height: 2rem;
  border-radius: var(--radius);
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${transitions.fast};
  padding: 0;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      color: ${colors.primary};
      border-color: ${colors.primary};
      background: ${colors.hoverTint};
    }
  }

  &:active:not(:disabled) {
    color: ${colors.primary};
    border-color: ${colors.primary};
    background: ${colors.hoverTint};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  }
`

export const PageInfo = styled.span`
  color: ${colors.textLightModeMuted};
  font-size: 0.85rem;
  padding: 0 0.5rem;
  white-space: nowrap;
`


// ============================================================================
// Re-export design tokens for convenience
// ============================================================================
export { colors, gradients, shadows, transitions, radius, transform }
